import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Upload, Database, FileText, AlertCircle, CheckCircle, Loader, Trash2, Network, Server, Smartphone } from 'lucide-react';
import toast from 'react-hot-toast';

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
    color: #1a1a1a;
  }

  p {
    color: #666;
    font-size: 1rem;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  background: none;
  color: ${props => props.$active ? '#3b82f6' : '#6b7280'};
  border-bottom: 3px solid ${props => props.$active ? '#3b82f6' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #3b82f6;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #374151;
  }

  input, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }

  .hint {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: 0.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &.primary {
    background: #3b82f6;
    color: white;

    &:hover:not(:disabled) {
      background: #2563eb;
    }
  }

  &.secondary {
    background: #f3f4f6;
    color: #374151;

    &:hover:not(:disabled) {
      background: #e5e7eb;
    }
  }

  &.danger {
    background: #ef4444;
    color: white;

    &:hover:not(:disabled) {
      background: #dc2626;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewSection = styled.div`
  margin-top: 2rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #1a1a1a;
  }
`;

const LogTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th {
    background: #f9fafb;
    padding: 0.75rem;
    text-align: left;
    font-weight: 600;
    color: #374151;
    border-bottom: 2px solid #e5e7eb;
  }

  td {
    padding: 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    color: #1a1a1a;
  }

  tr:hover {
    background: #f9fafb;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;

  &.success {
    background: #d1fae5;
    color: #065f46;
  }

  &.error {
    background: #fee2e2;
    color: #991b1b;
  }

  &.processing {
    background: #fef3c7;
    color: #92400e;
  }
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HistoryItem = styled.div`
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid ${props =>
    props.$status === 'completed' ? '#10b981' :
    props.$status === 'failed' ? '#ef4444' : '#f59e0b'};

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .title {
    font-weight: 600;
    color: #1a1a1a;
  }

  .meta {
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    color: #6b7280;
  }
`;

function LogManagementPage() {
  const [activeTab, setActiveTab] = useState('upload');
  const [logType, setLogType] = useState('nginx');
  const [filePath, setFilePath] = useState('/var/log/nginx/access.log');
  const [pm2AppName, setPm2AppName] = useState('ar-client');
  const [useSudo, setUseSudo] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);

  // 로그 조회 상태
  const [viewLogType, setViewLogType] = useState('nginx');
  const [viewLogs, setViewLogs] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [logLevelFilter, setLogLevelFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const logsPerPage = 50;

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    } else if (activeTab === 'nginx' || activeTab === 'pm2') {
      // 탭에 맞게 로그 타입 설정
      if (activeTab === 'nginx') {
        setViewLogType('nginx');
      } else if (activeTab === 'pm2') {
        setViewLogType('pm2');
      }
      fetchViewLogs();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'nginx' || activeTab === 'pm2') {
      fetchViewLogs();
    }
  }, [viewLogType, currentPage, searchQuery, statusFilter, logLevelFilter]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get('/api/logs/history');
      if (response.data.success) {
        setUploadHistory(response.data.history);
      }
    } catch (error) {
      console.error('히스토리 조회 실패:', error);
      toast.error('히스토리 조회에 실패했습니다.');
    }
  };

  const fetchViewLogs = async () => {
    setViewLoading(true);
    try {
      const params = new URLSearchParams({
        logType: viewLogType,
        limit: logsPerPage,
        offset: (currentPage - 1) * logsPerPage
      });

      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status_code', statusFilter);
      if (logLevelFilter) params.append('log_level', logLevelFilter);

      const response = await axios.get(`/api/logs/query?${params}`);

      if (response.data.success) {
        setViewLogs(response.data.logs);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
      }
    } catch (error) {
      console.error('로그 조회 실패:', error);
      toast.error(error.response?.data?.message || '로그 조회에 실패했습니다.');
    } finally {
      setViewLoading(false);
    }
  };

  const handleLogTypeChange = (newLogType) => {
    setLogType(newLogType);
    // 로그 타입에 따라 기본 경로 설정
    if (newLogType === 'nginx') {
      setFilePath('/var/log/nginx/access.log');
    } else if (newLogType === 'nginx-error') {
      setFilePath('/var/log/nginx/error.log');
    } else if (newLogType === 'pm2') {
      setFilePath(`/root/.pm2/logs/${pm2AppName}-out.log`);
    }
  };

  const handlePm2AppNameChange = (newAppName) => {
    setPm2AppName(newAppName);
    if (logType === 'pm2') {
      setFilePath(`/root/.pm2/logs/${newAppName}-out.log`);
    }
  };

  const handlePreview = async () => {
    if (!filePath) {
      toast.error('파일 경로를 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/logs/read', {
        logType,
        filePath,
        useSudo
      });

      if (response.data.success) {
        setPreview(response.data);
        toast.success(`${response.data.totalLines}줄의 로그를 찾았습니다. (파싱 성공률: ${response.data.successRate})`);
      }
    } catch (error) {
      console.error('로그 읽기 실패:', error);
      toast.error(error.response?.data?.message || '로그 파일을 읽을 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!filePath) {
      toast.error('파일 경로를 입력해주세요.');
      return;
    }

    if (!window.confirm(`${logType} 로그를 데이터베이스에 업로드하시겠습니까?`)) {
      return;
    }

    setLoading(true);
    const toastId = toast.loading('로그를 업로드 중입니다... (스트림 처리 중)');

    try {
      const response = await axios.post('/api/logs/upload', {
        logType,
        filePath,
        useSudo
      });

      if (response.data.success) {
        toast.success(
          `업로드 완료! ${response.data.successCount}개 성공, ${response.data.errorCount}개 실패`,
          { id: toastId }
        );
        setPreview(null);
        setFilePath('');
        setActiveTab('history');
      }
    } catch (error) {
      console.error('로그 업로드 실패:', error);
      toast.error(
        error.response?.data?.message || '로그 업로드에 실패했습니다.',
        { id: toastId }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClearLog = async () => {
    if (!filePath) {
      toast.error('파일 경로를 입력해주세요.');
      return;
    }

    const confirmMessage = `⚠️ 경고!\n\n파일: ${filePath}\n\n이 로그 파일의 내용을 완전히 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // 이중 확인
    const doubleConfirm = window.prompt('정말로 삭제하시려면 "DELETE"를 입력하세요:');
    if (doubleConfirm !== 'DELETE') {
      toast.error('취소되었습니다.');
      return;
    }

    setLoading(true);
    const toastId = toast.loading('로그 파일을 초기화하는 중...');

    try {
      const response = await axios.post('/api/logs/clear', {
        filePath,
        useSudo
      });

      if (response.data.success) {
        const sizeInMB = (response.data.previousSize / 1024 / 1024).toFixed(2);
        toast.success(
          `로그 파일이 초기화되었습니다! (이전 크기: ${sizeInMB}MB)`,
          { id: toastId }
        );
        setPreview(null);
      }
    } catch (error) {
      console.error('로그 초기화 실패:', error);
      toast.error(
        error.response?.data?.message || '로그 파일 초기화에 실패했습니다.',
        { id: toastId }
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const getStatusClass = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 300 && statusCode < 400) return 'info';
    if (statusCode >= 400 && statusCode < 500) return 'warn';
    if (statusCode >= 500) return 'error';
    return 'info';
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Container>
      <Header>
        <h1>로그 관리</h1>
        <p>Nginx 및 PM2 로그 파일을 데이터베이스로 가져옵니다.</p>
      </Header>

      <TabContainer>
        <Tab $active={activeTab === 'upload'} onClick={() => setActiveTab('upload')}>
          <Upload size={20} /> 로그 업로드
        </Tab>
        <Tab $active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
          <Database size={20} /> 업로드 히스토리
        </Tab>
        <Tab $active={activeTab === 'nginx'} onClick={() => setActiveTab('nginx')}>
          <FileText size={20} /> Nginx 로그
        </Tab>
        <Tab $active={activeTab === 'pm2'} onClick={() => setActiveTab('pm2')}>
          <FileText size={20} /> PM2 로그
        </Tab>
        <Tab $active={activeTab === 'protocol'} onClick={() => setActiveTab('protocol')}>
          <Network size={20} /> 통신 프로토콜 가이드
        </Tab>
      </TabContainer>

      {activeTab === 'upload' && (
        <Card>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            로그 파일 업로드
          </h2>

          <FormGroup>
            <label>로그 타입</label>
            <select value={logType} onChange={(e) => handleLogTypeChange(e.target.value)}>
              <option value="nginx">Nginx Access Log (access.log)</option>
              <option value="nginx-error">Nginx Error Log (error.log)</option>
              <option value="pm2">PM2 로그</option>
            </select>
          </FormGroup>

          {logType === 'pm2' && (
            <FormGroup>
              <label>PM2 앱 이름</label>
              <input
                type="text"
                value={pm2AppName}
                onChange={(e) => handlePm2AppNameChange(e.target.value)}
                placeholder="ar-client"
              />
              <p className="hint">
                PM2 프로세스 이름을 입력하세요. 로그 파일 경로가 자동으로 업데이트됩니다.
                예: ar-client → /root/.pm2/logs/ar-client-out.log
              </p>
            </FormGroup>
          )}

          <FormGroup>
            <label>파일 경로</label>
            <input
              type="text"
              value={filePath}
              onChange={(e) => setFilePath(e.target.value)}
              placeholder="/var/log/nginx/access.log"
            />
            <p className="hint">
              리눅스 서버의 로그 파일 절대 경로를 입력하세요.
              {logType === 'nginx' && ' 예: /var/log/nginx/access.log, /var/log/nginx/error.log'}
              {logType === 'pm2' && ' 예: ~/.pm2/logs/app-out.log, ~/.pm2/logs/app-error.log'}
            </p>
          </FormGroup>

          <FormGroup>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={useSudo}
                onChange={(e) => setUseSudo(e.target.checked)}
                style={{ width: 'auto', cursor: 'pointer' }}
              />
              <span>sudo 권한 사용 (권한이 필요한 로그 파일)</span>
            </label>
            <p className="hint" style={{ color: '#dc2626' }}>
              ⚠️ sudo 사용 시 서버에서 passwordless sudo 설정이 필요합니다.
            </p>
          </FormGroup>

          <ButtonGroup>
            <Button className="secondary" onClick={handlePreview} disabled={loading}>
              {loading ? <Loader className="animate-spin" size={20} /> : <FileText size={20} />}
              미리보기
            </Button>
            <Button className="primary" onClick={handleUpload} disabled={loading || !preview}>
              {loading ? <Loader className="animate-spin" size={20} /> : <Upload size={20} />}
              데이터베이스에 업로드
            </Button>
            <Button className="danger" onClick={handleClearLog} disabled={loading}>
              {loading ? <Loader className="animate-spin" size={20} /> : <Trash2 size={20} />}
              로그 파일 초기화
            </Button>
          </ButtonGroup>

          {preview && (
            <PreviewSection>
              <h3>
                미리보기 ({preview.previewLines}/{preview.totalLines}줄)
              </h3>
              <div style={{ overflowX: 'auto' }}>
                {logType === 'nginx' ? (
                  <LogTable>
                    <thead>
                      <tr>
                        <th>시간</th>
                        <th>IP</th>
                        <th>메소드</th>
                        <th>경로</th>
                        <th>상태</th>
                        <th>크기</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((log, index) => (
                        <tr key={index}>
                          <td>{log.timestamp ? formatDate(log.timestamp) : '-'}</td>
                          <td>{log.ip_address || '-'}</td>
                          <td>{log.request_method || '-'}</td>
                          <td>{log.request_path || '-'}</td>
                          <td>{log.status_code || '-'}</td>
                          <td>{log.response_size ? `${log.response_size} bytes` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </LogTable>
                ) : logType === 'nginx-error' ? (
                  <LogTable>
                    <thead>
                      <tr>
                        <th>시간</th>
                        <th>레벨</th>
                        <th>IP</th>
                        <th>메시지</th>
                        <th>경로</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((log, index) => (
                        <tr key={index}>
                          <td>{log.timestamp ? formatDate(log.timestamp) : '-'}</td>
                          <td>
                            <StatusBadge className={(log.log_level || 'error').toLowerCase()}>
                              {log.log_level || 'ERROR'}
                            </StatusBadge>
                          </td>
                          <td>{log.ip_address || '-'}</td>
                          <td style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {log.message || '-'}
                          </td>
                          <td>{log.request_path || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </LogTable>
                ) : (
                  <LogTable>
                    <thead>
                      <tr>
                        <th>시간</th>
                        <th>레벨</th>
                        <th>앱</th>
                        <th>메시지</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((log, index) => (
                        <tr key={index}>
                          <td>{log.timestamp ? formatDate(log.timestamp) : '-'}</td>
                          <td>
                            <StatusBadge className={(log.log_level || 'info').toLowerCase()}>
                              {log.log_level || 'INFO'}
                            </StatusBadge>
                          </td>
                          <td>{log.app_name || '-'}</td>
                          <td style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {log.message || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </LogTable>
                )}
              </div>
            </PreviewSection>
          )}
        </Card>
      )}

      {activeTab === 'history' && (
        <Card>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            업로드 히스토리
          </h2>

          <HistoryList>
            {uploadHistory.map((item) => (
              <HistoryItem key={item.id} $status={item.upload_status}>
                <div className="header">
                  <div className="title">
                    {item.log_type.toUpperCase()} - {item.file_path}
                  </div>
                  <StatusBadge className={item.upload_status}>
                    {item.upload_status === 'completed' && <CheckCircle size={14} />}
                    {item.upload_status === 'failed' && <AlertCircle size={14} />}
                    {item.upload_status}
                  </StatusBadge>
                </div>
                <div className="meta">
                  <span>전체: {item.total_lines}줄</span>
                  <span>성공: {item.success_count}</span>
                  <span>실패: {item.error_count}</span>
                  <span>업로드: {formatDate(item.created_at)}</span>
                  {item.uploaded_by_username && <span>작업자: {item.uploaded_by_username}</span>}
                </div>
              </HistoryItem>
            ))}
          </HistoryList>
        </Card>
      )}

      {activeTab === 'nginx' && (
        <Card>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            Nginx 로그 조회
          </h2>

          <FormGroup>
            <label>로그 타입</label>
            <select value={viewLogType} onChange={(e) => { setViewLogType(e.target.value); setCurrentPage(1); }}>
              <option value="nginx">Nginx Access Log</option>
              <option value="nginx-error">Nginx Error Log</option>
            </select>
          </FormGroup>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <FormGroup style={{ flex: '1', minWidth: '200px' }}>
              <label>검색</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder={viewLogType === 'nginx' ? 'IP 주소 또는 경로 검색...' : '메시지 검색...'}
              />
            </FormGroup>

            {viewLogType === 'nginx' && (
              <FormGroup style={{ width: '150px' }}>
                <label>상태 코드</label>
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="">전체</option>
                  <option value="200">200 OK</option>
                  <option value="301">301 Redirect</option>
                  <option value="302">302 Redirect</option>
                  <option value="304">304 Not Modified</option>
                  <option value="400">400 Bad Request</option>
                  <option value="401">401 Unauthorized</option>
                  <option value="403">403 Forbidden</option>
                  <option value="404">404 Not Found</option>
                  <option value="500">500 Server Error</option>
                  <option value="502">502 Bad Gateway</option>
                  <option value="503">503 Unavailable</option>
                </select>
              </FormGroup>
            )}

            {(viewLogType === 'nginx-error' || viewLogType === 'pm2') && (
              <FormGroup style={{ width: '150px' }}>
                <label>로그 레벨</label>
                <select value={logLevelFilter} onChange={(e) => { setLogLevelFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="">전체</option>
                  <option value="error">ERROR</option>
                  <option value="warn">WARN</option>
                  <option value="info">INFO</option>
                  <option value="debug">DEBUG</option>
                </select>
              </FormGroup>
            )}
          </div>

          {viewLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              로딩 중...
            </div>
          ) : viewLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              조회된 로그가 없습니다.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                총 {totalCount.toLocaleString()}개 로그 (페이지 {currentPage} / {totalPages})
              </div>

              {viewLogType === 'nginx' ? (
                <LogTable>
                  <thead>
                    <tr>
                      <th>시간</th>
                      <th>IP</th>
                      <th>메서드</th>
                      <th>경로</th>
                      <th>상태</th>
                      <th>크기</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewLogs.map((log) => (
                      <tr key={log.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(log.timestamp)}</td>
                        <td>{log.ip_address}</td>
                        <td>
                          <StatusBadge className={log.request_method?.toLowerCase() || 'get'}>
                            {log.request_method || 'GET'}
                          </StatusBadge>
                        </td>
                        <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.request_path}
                        </td>
                        <td>
                          <StatusBadge className={getStatusClass(log.status_code)}>
                            {log.status_code}
                          </StatusBadge>
                        </td>
                        <td>{formatBytes(log.response_size)}</td>
                      </tr>
                    ))}
                  </tbody>
                </LogTable>
              ) : viewLogType === 'nginx-error' ? (
                <LogTable>
                  <thead>
                    <tr>
                      <th>시간</th>
                      <th>레벨</th>
                      <th>IP</th>
                      <th>메시지</th>
                      <th>경로</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewLogs.map((log) => (
                      <tr key={log.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(log.timestamp)}</td>
                        <td>
                          <StatusBadge className={(log.log_level || 'error').toLowerCase()}>
                            {(log.log_level || 'ERROR').toUpperCase()}
                          </StatusBadge>
                        </td>
                        <td>{log.ip_address || '-'}</td>
                        <td style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.message}
                        </td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.request_path || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </LogTable>
              ) : (
                <LogTable>
                  <thead>
                    <tr>
                      <th>시간</th>
                      <th>앱</th>
                      <th>레벨</th>
                      <th>메시지</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewLogs.map((log) => (
                      <tr key={log.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(log.timestamp)}</td>
                        <td>{log.app_name || '-'}</td>
                        <td>
                          <StatusBadge className={(log.log_level || 'info').toLowerCase()}>
                            {(log.log_level || 'INFO').toUpperCase()}
                          </StatusBadge>
                        </td>
                        <td style={{ maxWidth: '500px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.message}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </LogTable>
              )}

              {/* Pagination */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <Button
                  className="secondary"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  처음
                </Button>
                <Button
                  className="secondary"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  이전
                </Button>
                <span style={{ display: 'flex', alignItems: 'center', padding: '0 1rem', color: '#374151', fontWeight: '600' }}>
                  {currentPage} / {totalPages}
                </span>
                <Button
                  className="secondary"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  다음
                </Button>
                <Button
                  className="secondary"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  마지막
                </Button>
              </div>
            </>
          )}
        </Card>
      )}

      {activeTab === 'pm2' && (
        <Card>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            PM2 로그 조회
          </h2>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <FormGroup style={{ flex: '1', minWidth: '200px' }}>
              <label>검색</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="메시지 검색..."
              />
            </FormGroup>

            <FormGroup style={{ width: '150px' }}>
              <label>로그 레벨</label>
              <select value={logLevelFilter} onChange={(e) => { setLogLevelFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">전체</option>
                <option value="error">ERROR</option>
                <option value="warn">WARN</option>
                <option value="info">INFO</option>
                <option value="debug">DEBUG</option>
              </select>
            </FormGroup>
          </div>

          {viewLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              로딩 중...
            </div>
          ) : viewLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              조회된 로그가 없습니다.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                총 {totalCount.toLocaleString()}개 로그 (페이지 {currentPage} / {totalPages})
              </div>

              <LogTable>
                <thead>
                  <tr>
                    <th>시간</th>
                    <th>앱</th>
                    <th>레벨</th>
                    <th>메시지</th>
                  </tr>
                </thead>
                <tbody>
                  {viewLogs.map((log) => (
                    <tr key={log.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(log.timestamp)}</td>
                      <td>{log.app_name || '-'}</td>
                      <td>
                        <StatusBadge className={(log.log_level || 'info').toLowerCase()}>
                          {(log.log_level || 'INFO').toUpperCase()}
                        </StatusBadge>
                      </td>
                      <td style={{ maxWidth: '600px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </LogTable>

              {/* Pagination */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <Button
                  className="secondary"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  처음
                </Button>
                <Button
                  className="secondary"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  이전
                </Button>
                <span style={{ display: 'flex', alignItems: 'center', padding: '0 1rem', color: '#374151', fontWeight: '600' }}>
                  {currentPage} / {totalPages}
                </span>
                <Button
                  className="secondary"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  다음
                </Button>
                <Button
                  className="secondary"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  마지막
                </Button>
              </div>
            </>
          )}
        </Card>
      )}

      {activeTab === 'protocol' && (
        <ProtocolGuide />
      )}
    </Container>
  );
}

// 통신 프로토콜 가이드 컴포넌트
const ProtocolGuide = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);

  const clientToServerEvents = [
    {
      name: 'session:request',
      direction: '클라이언트 → 서버',
      description: '세션 정보 요청',
      parameters: 'None',
      response: 'session:data 또는 session:response',
      location: 'SocketIONetworkModule.cs:248, ARBarcodeScanner.cs',
      serverHandler: 'execute_prd.js:425-471',
      example: `// Unity Client
socket.Emit("session:request");

// Server Handler
socket.on('session:request', () => {
  const cur = store.get(String(roomCode));
  socket.emit('session:data', cur);
  if (cur.objects) socket.emit('objects:update', cur.objects);
  if (cur.problems) socket.emit('problems:update', cur.problems);
});`
    },
    {
      name: 'persona:chat',
      direction: '클라이언트 → 서버',
      description: 'AI 페르소나와 채팅 (Claude API)',
      parameters: `{
  requestId: string,
  playerId: string,
  playerName: string,
  arObjectId: string,
  userMessage: string,
  conversationHistory: object[]
}`,
      response: 'persona:chat:response (30초 타임아웃)',
      location: 'SocketIONetworkModule.cs:1110-1127',
      serverHandler: 'execute_prd.js:772-837',
      example: `// Unity Client
var chatData = new {
  requestId = "socket_id_timestamp",
  playerId = socket.Id,
  playerName = "Player",
  arObjectId = "object_book",
  userMessage = "What is this?",
  conversationHistory = new object[] { }
};
socket.Emit("persona:chat", chatData);

// Server Handler - 호스트에게 전달
socket.on('persona:chat', (payload, ack) => {
  const finalRequestId = requestId || \`\${socket.id}_\${Date.now()}\`;
  ns.to(roomCode).emit('persona:chat:request', forwardPayload);
  ack({ ok: true, message: '호스트에게 요청 전달됨' });
});`
    },
    {
      name: 'answer:submit',
      direction: '클라이언트 → 서버',
      description: '퀴즈 답변 제출 (보상 아이템 획득)',
      parameters: `{
  questionId: number,
  answer: string,
  timeSpent: number
}`,
      response: 'answer:result (ACK 응답)',
      location: 'ARQuizDataManager.cs',
      serverHandler: 'execute_prd.js:626-769',
      example: `// Unity Client
NetworkManager.Instance.Send("answer:submit", payload, onSuccess, onError);

// Server Handler
socket.on('answer:submit', async (payload, ack) => {
  const { questionId, answer, timeSpent } = payload;
  const problem = sess.problems.find(p => p.questionId === questionId);
  const isCorrect = (correctAnswer === answer);

  // 점수 계산 및 보상 선택
  const scoreDelta = isCorrect ? 10000 : -500;
  const rewardItem = selectRandomReward(scoreDelta);

  // DB 저장 및 응답
  ack({ ok: true, result });
  socket.emit('answer:result', result);
  ns.to(roomCode).emit('reward:item', { item: rewardItem });
});`
    },
    {
      name: 'objects:update',
      direction: '클라이언트 → 서버',
      description: 'AR 오브젝트 목록 업데이트 (호스트→서버→브로드캐스트)',
      parameters: `Array of objects:
{
  id: string,
  name: string,
  type: string,
  modelPath: string
}`,
      response: '방 전체에 objects:update 브로드캐스트',
      location: 'ARBarcodeScanner.cs',
      serverHandler: 'execute_prd.js:473-520',
      example: `// Server Handler
socket.on('objects:update', (objects) => {
  const cur = store.update(String(roomCode), { objects });
  ns.to(roomCode).emit('objects:update', cur.objects || []);
});`
    },
    {
      name: 'problems:update',
      direction: '클라이언트 → 서버',
      description: '퀴즈 문제 목록 업데이트 (호스트→서버→브로드캐스트)',
      parameters: `Array of problems:
{
  questionId: number,
  title: string,
  objectName: string,
  answer: string,
  answers: string[],
  timelimit: number
}`,
      response: '방 전체에 problems:update 브로드캐스트',
      location: 'ARBarcodeScanner.cs',
      serverHandler: 'execute_prd.js:522-597',
      example: `// Server Handler
socket.on('problems:update', (problems) => {
  const cur = store.update(String(roomCode), { problems });
  ns.to(roomCode).emit('problems:update', cur.problems || []);
});`
    },
    {
      name: 'session:start',
      direction: '클라이언트 → 서버',
      description: '세션 시작 신호 (방 전체 브로드캐스트)',
      parameters: 'payload: object (optional)',
      response: '방 전체에 session:start 브로드캐스트',
      location: 'ARBarcodeScanner.cs',
      serverHandler: 'execute_prd.js:599-602',
      example: `// Server Handler
socket.on('session:start', (payload) => {
  ns.to(roomCode).emit('session:start', payload || {});
});`
    },
    {
      name: 'game:start',
      direction: '클라이언트 → 서버',
      description: '게임 시작 카운트다운 (방 전체 브로드캐스트)',
      parameters: 'payload: object (optional)',
      response: '방 전체에 game:start 브로드캐스트',
      location: 'ARBarcodeScanner.cs',
      serverHandler: 'execute_prd.js:604-607',
      example: `// Server Handler
socket.on('game:start', (payload) => {
  ns.to(roomCode).emit('game:start', payload || {});
});`
    },
    {
      name: 'qr_scanned',
      direction: '클라이언트 → 서버',
      description: 'QR 코드 스캔 이벤트 (방 내 다른 플레이어에게 알림)',
      parameters: 'data: object (QR 스캔 정보)',
      response: 'qr_scanned_ack + 방 내 다른 플레이어에게 qr_scanned 브로드캐스트',
      location: 'Unity Client',
      serverHandler: 'execute_prd.js:610-623',
      example: `// Server Handler
socket.on('qr_scanned', (data) => {
  socket.to(roomCode).emit('qr_scanned', data);
  socket.emit('qr_scanned_ack', { success: true, roomCode });
});`
    },
    {
      name: 'persona:chat:response',
      direction: '호스트(웹) → 서버 → 클라이언트',
      description: 'AI 페르소나 응답 (호스트가 Claude API 호출 후 Unity에게 전달)',
      parameters: `{
  requestId: string,
  playerId: string,
  ok: boolean,
  objectName: string,
  response: string,
  error: string,
  usage: { input_tokens, output_tokens, ... }
}`,
      response: '해당 playerId에게 유니캐스트 전송',
      location: 'Web Host',
      serverHandler: 'execute_prd.js:840-908',
      example: `// Server Handler (호스트 → Unity)
socket.on('persona:chat:response', (payload) => {
  const { playerId } = payload;
  if (playerId) {
    ns.to(playerId).emit('persona:chat:response', responsePayload);
  } else {
    ns.to(roomCode).emit('persona:chat:response', payload);
  }
});`
    }
  ];

  const serverToClientEvents = [
    {
      name: 'session:response',
      direction: '서버 → 클라이언트',
      description: '초기 세션 응답 (서버 UUID)',
      parameters: `{
  sessionUuid: string
}`,
      trigger: '소켓 연결 직후',
      location: 'SocketIONetworkModule.cs:363-382',
      serverEmit: 'execute_prd.js - 자동 전송 없음 (레거시)',
      example: `// Unity Client Handler
socket.OnUnityThread("session:response", (response) => {
  var data = JsonConvert.DeserializeObject<SessionResponse>(response.ToString());
  sessionUuid = data.sessionUuid;
});`
    },
    {
      name: 'session:data',
      direction: '서버 → 클라이언트',
      description: '세션 정보 및 상태 업데이트',
      parameters: 'Session object (varies)',
      trigger: 'session:request 또는 자동 전송',
      location: 'ARBarcodeScanner.cs, SocketIOConnectionTest.cs',
      serverEmit: 'execute_prd.js:367, 440',
      example: `// Unity Client Handler
NetworkManager.Instance.Subscribe("session:data", (data) => {
  var sessionData = JsonConvert.DeserializeObject<SessionData>(data.Data);
  // 세션 정보 처리
});

// Server Emit (연결 시 자동 전송)
const sess = store.get(String(roomCode));
socket.emit('session:data', sess);`
    },
    {
      name: 'session:start',
      direction: '서버 → 클라이언트',
      description: '세션 시작 알림',
      parameters: 'payload: object (optional)',
      trigger: '호스트가 session:start 전송',
      location: 'ARBarcodeScanner.cs',
      serverEmit: 'execute_prd.js:601',
      example: `// Unity Client Handler
NetworkManager.Instance.Subscribe("session:start", (data) => {
  LogDebug("[EVENT] session:start");
  // 게임 세션 시작 처리
});`
    },
    {
      name: 'player:joined',
      direction: '서버 → 클라이언트',
      description: '플레이어 입장 알림',
      parameters: `{
  id: string,
  name: string,
  role: string,
  ip: string,
  sessionId: string
}`,
      trigger: '새 플레이어 연결 (Host 제외)',
      location: 'ARBarcodeScanner.cs',
      serverEmit: 'execute_prd.js:340',
      example: `// Unity Client Handler
NetworkManager.Instance.Subscribe("player:joined", (data) => {
  var player = JsonConvert.DeserializeObject<Player>(data.Data);
  LogDebug($"[EVENT] player:joined: {player.name}");
});

// Server Emit (중복 연결 5초 이내 방지)
ns.in(roomCode).emit('player:joined', player);`
    },
    {
      name: 'player:left',
      direction: '서버 → 클라이언트',
      description: '플레이어 퇴장 알림',
      parameters: `{
  id: string
}`,
      trigger: '플레이어 연결 종료',
      location: 'ARBarcodeScanner.cs',
      serverEmit: 'execute_prd.js:419',
      example: `// Unity Client Handler
NetworkManager.Instance.Subscribe("player:left", (data) => {
  var leftPlayer = JsonConvert.DeserializeObject<PlayerLeft>(data.Data);
  LogDebug($"[EVENT] player:left: {leftPlayer.id}");
});

// Server Emit
ns.to(roomCode).emit('player:left', { id: socket.id });`
    },
    {
      name: 'objects:update',
      direction: '서버 → 클라이언트',
      description: 'AR 오브젝트 목록 업데이트',
      parameters: `Array of objects:
string[] (object names) 또는
Array<{ id, name, type, modelPath, ... }>`,
      trigger: 'objects:update 수신 또는 session:request',
      location: 'ARBarcodeScanner.cs (OnObjectsUpdateReceived)',
      serverEmit: 'execute_prd.js:449, 516',
      example: `// Unity Client Handler
NetworkManager.Instance.Subscribe("objects:update", (data) => {
  // Option 1: String array
  string[] objectNames = JsonConvert.DeserializeObject<string[]>(data.Data);

  // Option 2: Object array
  var objectList = JsonConvert.DeserializeObject<List<dynamic>>(data.Data);
});

// Server Emit
socket.emit('objects:update', cur.objects);
ns.to(roomCode).emit('objects:update', cur.objects || []);`
    },
    {
      name: 'problems:update',
      direction: '서버 → 클라이언트',
      description: '퀴즈 문제 목록 업데이트',
      parameters: `Array of problems:
Array<{
  questionId, title, objectName,
  answer, answers, timelimit, ...
}>`,
      trigger: 'problems:update 수신 또는 session:request',
      location: 'ARBarcodeScanner.cs (OnProblemsUpdateReceived)',
      serverEmit: 'execute_prd.js:460, 593',
      example: `// Unity Client Handler
NetworkManager.Instance.Subscribe("problems:update", (data) => {
  var problems = JsonConvert.DeserializeObject<List<Problem>>(data.Data);
  // 문제 목록 업데이트
});

// Server Emit
socket.emit('problems:update', cur.problems);
ns.to(roomCode).emit('problems:update', cur.problems || []);`
    },
    {
      name: 'game:start',
      direction: '서버 → 클라이언트',
      description: '게임 시작 알림',
      parameters: 'payload: object (optional)',
      trigger: '호스트가 game:start 전송',
      location: 'ARQuizDataManager.cs',
      serverEmit: 'execute_prd.js:606',
      example: `// Unity Client Handler
NetworkManager.Instance.Subscribe("game:start", (data) => {
  // 게임 시작 카운트다운
});`
    },
    {
      name: 'answer:result',
      direction: '서버 → 클라이언트',
      description: '퀴즈 답변 결과 (정답/오답, 점수, 보상)',
      parameters: `{
  questionId: number,
  playerId: string,
  playerName: string,
  correct: boolean,
  scoreDelta: number,
  reward: { id, name, rarity, effect, value },
  timestamp: string
}`,
      trigger: 'answer:submit 처리 후',
      location: 'ARQuizDataManager.cs',
      serverEmit: 'execute_prd.js:743',
      example: `// Unity Client Handler
NetworkManager.Instance.Subscribe("answer:result", (data) => {
  var result = JsonConvert.DeserializeObject<AnswerResult>(data.Data);
  if (result.correct) {
    ShowCorrectUI(result.scoreDelta, result.reward);
  } else {
    ShowIncorrectUI(result.scoreDelta);
  }
});

// Server Emit
socket.emit('answer:result', result);`
    },
    {
      name: 'reward:item',
      direction: '서버 → 클라이언트 (방 전체)',
      description: '보상 아이템 획득 브로드캐스트',
      parameters: `{
  playerId: string,
  playerName: string,
  questionId: number,
  item: { id, name, rarity, effect, value },
  timestamp: string
}`,
      trigger: 'answer:submit 정답 시',
      location: 'ARQuizDataManager.cs',
      serverEmit: 'execute_prd.js:747',
      example: `// Unity Client Handler
NetworkManager.Instance.Subscribe("reward:item", (data) => {
  var reward = JsonConvert.DeserializeObject<RewardData>(data.Data);
  ShowRewardAnimation(reward.item);
});

// Server Emit (방 전체)
ns.to(roomCode).emit('reward:item', {
  playerId: socket.id,
  playerName: name,
  item: rewardItem
});`
    },
    {
      name: 'persona:chat:response',
      direction: '서버 → 클라이언트',
      description: 'AI 페르소나 채팅 응답 (Claude API)',
      parameters: `{
  requestId: string,
  playerId: string,
  ok: boolean,
  objectName: string,
  response: string,
  error: string,
  usage: {
    input_tokens: number,
    cache_creation_input_tokens: number,
    cache_read_input_tokens: number,
    output_tokens: number,
    service_tier: string
  }
}`,
      trigger: '호스트가 Claude API 응답 전달',
      location: 'SocketIONetworkModule.cs:385-481',
      serverEmit: 'execute_prd.js:891',
      example: `// Unity Client Handler
socket.OnUnityThread("persona:chat:response", (response) => {
  var broadcasts = JsonConvert.DeserializeObject<PersonaBroadcastData[]>(response.ToString());
  foreach (var broadcast in broadcasts) {
    if (broadcast.ok && !string.IsNullOrEmpty(broadcast.response)) {
      ShowChatResponse(broadcast.objectName, broadcast.response);
    }
  }
});

// Server Emit (유니캐스트 - 특정 플레이어)
ns.to(playerId).emit('persona:chat:response', responsePayload);`
    },
    {
      name: 'persona:response',
      direction: '서버 → 클라이언트',
      description: 'AI 페르소나 응답 (레거시)',
      parameters: 'Response data',
      trigger: '레거시 호환성',
      location: 'SocketIONetworkModule.cs:484-497',
      serverEmit: 'N/A (레거시)',
      example: `// Unity Client Handler (레거시 지원)
socket.OnUnityThread("persona:response", (response) => {
  // 구버전 호환
});`
    },
    {
      name: 'score:update',
      direction: '서버 → 클라이언트 (방 전체)',
      description: '점수 업데이트 브로드캐스트',
      parameters: `{
  playerId: string,
  playerName: string,
  scoreDelta: number,
  timestamp: string
}`,
      trigger: 'answer:submit 처리 후',
      location: 'Unity Client (미구현)',
      serverEmit: 'execute_prd.js:758',
      example: `// Server Emit (방 전체)
ns.to(roomCode).emit('score:update', {
  playerId: socket.id,
  playerName: name,
  scoreDelta: scoreDelta
});`
    },
    {
      name: 'qr_scanned',
      direction: '서버 → 클라이언트',
      description: 'QR 스캔 이벤트 (같은 방의 다른 플레이어)',
      parameters: 'data: object (QR 스캔 정보)',
      trigger: '다른 플레이어가 QR 스캔',
      location: 'Unity Client',
      serverEmit: 'execute_prd.js:615',
      example: `// Server Emit (방 내 다른 플레이어에게)
socket.to(roomCode).emit('qr_scanned', data);`
    },
    {
      name: 'qr_scanned_ack',
      direction: '서버 → 클라이언트',
      description: 'QR 스캔 확인 응답',
      parameters: `{
  success: boolean,
  roomCode: string,
  message: string
}`,
      trigger: 'qr_scanned 수신 후',
      location: 'Unity Client',
      serverEmit: 'execute_prd.js:618',
      example: `// Server Emit
socket.emit('qr_scanned_ack', {
  success: true,
  roomCode,
  message: 'QR scan received'
});`
    }
  ];

  const connectionInfo = {
    serverUrl: 'https://play.antsnest.co.kr',
    namespace: '/sessions',
    fullUri: 'https://play.antsnest.co.kr/sessions',
    transport: {
      android: 'Polling (HTTP long-polling)',
      other: 'WebSocket (with fallback to Polling)'
    },
    queryParams: {
      roomCode: '6-digit room code (e.g., "661164")',
      role: 'Player role (default: "player")',
      name: 'Player name (default: "UnityClient")'
    },
    settings: {
      reconnection: 'Enabled (max 5 attempts, 2000ms delay)',
      connectionTimeout: '30 seconds',
      engineIOVersion: 'V4',
      path: '/socket.io/'
    }
  };

  return (
    <div>
      <Card>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
          Socket.IO 통신 프로토콜 가이드
        </h2>
        <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
          Unity 클라이언트와 Node.js 서버 간의 Socket.IO 통신 프로토콜 명세
        </p>

        {/* 연결 정보 */}
        <div style={{
          background: '#f3f4f6',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          fontFamily: 'monospace',
          fontSize: '0.875rem'
        }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Server size={20} /> 연결 설정
          </h3>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <div><strong>Server URL:</strong> {connectionInfo.serverUrl}</div>
            <div><strong>Namespace:</strong> {connectionInfo.namespace}</div>
            <div><strong>Full URI:</strong> {connectionInfo.fullUri}</div>
            <div><strong>Transport (Android):</strong> {connectionInfo.transport.android}</div>
            <div><strong>Transport (Other):</strong> {connectionInfo.transport.other}</div>
            <div style={{ marginTop: '1rem' }}><strong>Query Parameters:</strong></div>
            <div style={{ paddingLeft: '1rem' }}>
              <div>• roomCode: {connectionInfo.queryParams.roomCode}</div>
              <div>• role: {connectionInfo.queryParams.role}</div>
              <div>• name: {connectionInfo.queryParams.name}</div>
            </div>
            <div style={{ marginTop: '1rem' }}><strong>Settings:</strong></div>
            <div style={{ paddingLeft: '1rem' }}>
              <div>• Reconnection: {connectionInfo.settings.reconnection}</div>
              <div>• Timeout: {connectionInfo.settings.connectionTimeout}</div>
              <div>• Engine.IO: {connectionInfo.settings.engineIOVersion}</div>
            </div>
          </div>
        </div>

        {/* 클라이언트 → 서버 이벤트 */}
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Smartphone size={20} color="#3b82f6" /> 클라이언트 → 서버 이벤트
        </h3>
        <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
          {clientToServerEvents.map((event, idx) => (
            <div
              key={idx}
              style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setSelectedEvent(selectedEvent === `c2s-${idx}` ? null : `c2s-${idx}`)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '1rem', color: '#1f2937', marginBottom: '0.25rem' }}>
                    {event.name}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    {event.description}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    📍 {event.location}
                  </div>
                </div>
                <StatusBadge className="info" style={{ whiteSpace: 'nowrap' }}>
                  {event.direction}
                </StatusBadge>
              </div>

              {selectedEvent === `c2s-${idx}` && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Parameters:</strong>
                    <pre style={{
                      background: '#1f2937',
                      color: '#e5e7eb',
                      padding: '1rem',
                      borderRadius: '6px',
                      overflow: 'auto',
                      fontSize: '0.75rem',
                      marginTop: '0.5rem'
                    }}>
                      {event.parameters}
                    </pre>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Response:</strong> {event.response}
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Server Handler:</strong> {event.serverHandler}
                  </div>
                  <div>
                    <strong>Example:</strong>
                    <pre style={{
                      background: '#1f2937',
                      color: '#e5e7eb',
                      padding: '1rem',
                      borderRadius: '6px',
                      overflow: 'auto',
                      fontSize: '0.75rem',
                      marginTop: '0.5rem'
                    }}>
                      {event.example}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 서버 → 클라이언트 이벤트 */}
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Server size={20} color="#10b981" /> 서버 → 클라이언트 이벤트
        </h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          {serverToClientEvents.map((event, idx) => (
            <div
              key={idx}
              style={{
                background: '#f0fdf4',
                border: '1px solid #d1fae5',
                borderRadius: '8px',
                padding: '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setSelectedEvent(selectedEvent === `s2c-${idx}` ? null : `s2c-${idx}`)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '1rem', color: '#1f2937', marginBottom: '0.25rem' }}>
                    {event.name}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    {event.description}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    📍 {event.location}
                  </div>
                </div>
                <StatusBadge className="success" style={{ whiteSpace: 'nowrap' }}>
                  {event.direction}
                </StatusBadge>
              </div>

              {selectedEvent === `s2c-${idx}` && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #d1fae5' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Parameters:</strong>
                    <pre style={{
                      background: '#1f2937',
                      color: '#e5e7eb',
                      padding: '1rem',
                      borderRadius: '6px',
                      overflow: 'auto',
                      fontSize: '0.75rem',
                      marginTop: '0.5rem'
                    }}>
                      {event.parameters}
                    </pre>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Trigger:</strong> {event.trigger}
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Server Emit:</strong> {event.serverEmit}
                  </div>
                  <div>
                    <strong>Example:</strong>
                    <pre style={{
                      background: '#1f2937',
                      color: '#e5e7eb',
                      padding: '1rem',
                      borderRadius: '6px',
                      overflow: 'auto',
                      fontSize: '0.75rem',
                      marginTop: '0.5rem'
                    }}>
                      {event.example}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 주요 노트 */}
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: '#fef3c7',
          borderLeft: '4px solid #f59e0b',
          borderRadius: '8px'
        }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#92400e' }}>
            ⚠️ 주요 구현 노트
          </h3>
          <ul style={{ fontSize: '0.875rem', color: '#78350f', lineHeight: '1.6', paddingLeft: '1.5rem' }}>
            <li><strong>Android Transport:</strong> Polling만 사용 (SSL 인증서 문제로 WebSocket 비활성화)</li>
            <li><strong>Reconnection:</strong> 자동 재연결 지원 (최대 5회, 2초 간격)</li>
            <li><strong>Chat Requests:</strong> requestId로 요청-응답 매칭 (30초 타임아웃)</li>
            <li><strong>Token Usage:</strong> persona:chat:response에 Claude API 토큰 사용량 포함</li>
            <li><strong>SSL Bypass:</strong> 모바일 플랫폼에서 자체 서명 인증서 허용</li>
            <li><strong>Timeout:</strong> SendPersonaChatAsync 30초, 일반 SendAsync 120초</li>
            <li><strong>Event Queueing:</strong> 연결 전 요청은 큐에 저장, 연결 후 처리</li>
            <li><strong>Unity Thread Safety:</strong> OnUnityThread 콜백으로 메인 스레드 UI 업데이트 보장</li>
            <li><strong>Query Parameters:</strong> roomCode는 연결 시 필수 (6자리 숫자 형식)</li>
            <li><strong>보상 시스템:</strong> 점수에 따라 legendary/epic/rare/common 등급 아이템 확률 지급</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default LogManagementPage;
